
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Link,
    Stack,
    Typography,
} from "@mui/material";
import ReactMarkdown from "react-markdown";

import type { WidgetProps } from "./types";

export function ProgramAccordion({ widget }: WidgetProps<"program_accordion">) {
    const { title, description, programs } = widget.props;

    return (
        <Box>
            {title && (
                <Typography variant="h3" sx={{ mb: description ? 1 : 2 }}>
                    {title}
                </Typography>
            )}
            {description && (
                <Typography variant="body1" sx={{ mb: 2, color: "text.secondary" }}>
                    {description}
                </Typography>
            )}
            <Stack spacing={1.25}>
                {programs.map((program, idx) => (
                    <Accordion
                        key={`${widget.id}-${idx}`}
                        disableGutters
                        elevation={0}
                        sx={{
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                            "&:before": { display: "none" },
                            "&.Mui-expanded": { my: 0 },
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                px: 2.5,
                                py: 0.5,
                                "& .MuiAccordionSummary-content": { my: 1.5 },
                            }}
                        >
                            <Stack>
                                <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                                    {program.title}
                                </Typography>
                                {program.summary && (
                                    <Typography
                                        variant="body2"
                                        sx={{ color: "text.secondary", mt: 0.25 }}
                                    >
                                        {program.summary}
                                    </Typography>
                                )}
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
                            <Box
                                sx={{
                                    "& > *:first-of-type": { mt: 0 },
                                    "& > *:last-child": { mb: 0 },
                                }}
                            >
                                <ReactMarkdown
                                    components={{
                                        h2: ({ children }) => (
                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    mt: 2.5,
                                                    mb: 1,
                                                    fontWeight: 600,
                                                    color: "primary.main",
                                                }}
                                            >
                                                {children}
                                            </Typography>
                                        ),
                                        h3: ({ children }) => (
                                            <Typography
                                                variant="subtitle2"
                                                sx={{ mt: 2, mb: 0.75, fontWeight: 600 }}
                                            >
                                                {children}
                                            </Typography>
                                        ),
                                        p: ({ children }) => (
                                            <Typography variant="body2" sx={{ mb: 1.5 }}>
                                                {children}
                                            </Typography>
                                        ),
                                        ul: ({ children }) => (
                                            <Box component="ul" sx={{ pl: 3, mb: 1.5 }}>
                                                {children}
                                            </Box>
                                        ),
                                        ol: ({ children }) => (
                                            <Box component="ol" sx={{ pl: 3, mb: 1.5 }}>
                                                {children}
                                            </Box>
                                        ),
                                        li: ({ children }) => (
                                            <Typography
                                                component="li"
                                                variant="body2"
                                                sx={{ mb: 0.4 }}
                                            >
                                                {children}
                                            </Typography>
                                        ),
                                        strong: ({ children }) => (
                                            <Box
                                                component="strong"
                                                sx={{ fontWeight: 600, color: "primary.main" }}
                                            >
                                                {children}
                                            </Box>
                                        ),
                                        a: ({ children, href }) => (
                                            <Link href={href} target="_blank" rel="noopener noreferrer">
                                                {children}
                                            </Link>
                                        ),
                                    }}
                                >
                                    {program.body}
                                </ReactMarkdown>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Stack>
        </Box>
    );
}
